import { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { TemplateLivePreview } from '@/components/TemplateLivePreview';
import {
  Users, ArrowRight, ExternalLink, Eye,
  Send, CheckCircle, Lock, Wand2, Layers3, Mic, MicOff,
  PenLine, Image as ImageIcon, FileText, Sparkles, Home, LayoutTemplate,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useAuthStore } from '@/store/authStore';
import { useActiveSite } from '@/hooks/useActiveSite';
import { getAnalyticsSummary } from '@/api/tenant';
import { getLeads } from '@/api/leads';
import { getTemplates, applyTemplateBySlug, type Template } from '@/api/templates';
import { getSiteBuildState } from '@/api/sites';
import { getProfile } from '@/api/profile';
import { chooseBuildRoute } from '@/lib/newSiteBuildRoute';
import { isBuildInProgress } from '@/lib/buildWizardProgress';
import { useVoiceInput } from '@/hooks/useVoiceInput';
import GuidedStartDialog from '@/components/onboarding/GuidedStartDialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/components/ui/use-toast';
import { formatRelativeTime } from '@/lib/utils';

const MONTSERRAT = { fontFamily: 'Montserrat, sans-serif' } as const;

const PLAN_LABELS: Record<string, string> = {
  free: 'Free',
  starter: 'Starter',
  pro: 'Pro',
  agency: 'Agency',
};

// Starter prompts for a brand-new (empty) site — each one matches the whole-site
// scaffold parser, so it builds a full site. Tapping fills the box first so the
// agent can add their brokerage name / city before hitting Build.
// Vibe-led starters (not template names) — they show that describing a LOOK gets a
// custom, non-templated design from the chat alone.
const START_PROMPTS = [
  'Warm, editorial site for a luxury coastal agent',
  'Bold and modern — deep navy with a gold accent',
  'Clean and minimal, lots of whitespace, photo-forward',
  'Refined and classic — serif headings, timeless feel',
];
// Quick edits for a site that already exists — these run as chat commands in the Studio.
const EDIT_PROMPTS = [
  'Add an about page',
  'Add a contact page',
  'Add an agents page',
  'Add a blog',
];

// Suggested next steps for an existing site — each one routes to the right surface.
// Deterministic actions (no AI surprise); a small, finite set, not a widget wall.
const NEXT_STEPS: { label: string; hint: string; icon: typeof PenLine; prompt?: string; to?: string }[] = [
  { label: 'Add your listings', hint: 'Connect MLS or feature properties', icon: ImageIcon, prompt: 'add my listings to the homepage' },
  { label: 'Write your About page', hint: 'Tell visitors who you are', icon: FileText, prompt: 'add an about page' },
  { label: 'Set your brand', hint: 'Colors, logo, and fonts', icon: PenLine, to: '/branding' },
];

function TemplateSkeleton() {
  return (
    <div className="rounded-xl border border-brand-border bg-white overflow-hidden animate-pulse">
      <div className="aspect-[4/3] bg-brand-lilac-2" />
      <div className="p-3 space-y-2">
        <div className="h-3.5 w-2/3 rounded bg-brand-border" />
        <div className="h-3 w-full rounded bg-brand-border" />
      </div>
    </div>
  );
}

interface TemplateCardProps {
  template: Template;
  onSelect: (template: Template) => void;
  isLoading: boolean;
}

// A branded "site preview" wireframe (browser chrome + faux hero/content bars). Always
// the base layer behind a card's live preview/photo, so a card NEVER shows a broken
// image or a blank frame — worst case it reads as a tasteful site-preview placeholder.
function TemplateWireframe({ name }: { name: string }) {
  return (
    <div aria-hidden className="absolute inset-0 flex flex-col">
      <div className="flex items-center gap-1.5 px-3 py-2">
        <span className="h-1.5 w-1.5 rounded-full bg-brand-sand/80" />
        <span className="h-1.5 w-1.5 rounded-full bg-brand-lavender/80" />
        <span className="h-1.5 w-1.5 rounded-full bg-brand-teal/80" />
      </div>
      <div className="flex flex-1 flex-col gap-2 px-4 pb-4">
        <div className="h-1/2 rounded-lg bg-white/10 ring-1 ring-white/10" />
        <div className="h-2 w-2/3 rounded-full bg-white/15" />
        <div className="h-2 w-1/2 rounded-full bg-white/10" />
        <div className="mt-auto flex items-center gap-1.5">
          <LayoutTemplate className="h-3.5 w-3.5 text-brand-lavender/80" />
          <span className="truncate text-[11px] font-medium text-brand-lavender/80">{name}</span>
        </div>
      </div>
    </div>
  );
}

// TemplateLivePreview + its helpers now live in a shared component so the wizard template picker
// renders templates the SAME way (they used to differ → stale wizard thumbnails). See
// components/TemplateLivePreview.tsx.

function TemplateCard({ template, onSelect, isLoading }: TemplateCardProps) {
  const isLocked = template.locked;
  const isApplied = template.is_applied;

  return (
    <div
      className={`
        group relative cursor-pointer overflow-hidden rounded-2xl border-[1.5px] bg-white shadow-[4px_4px_0_0_rgba(16,3,42,0.07)] transition-all duration-200 hover:-translate-x-0.5 hover:-translate-y-0.5 hover:shadow-[7px_7px_0_0_rgba(16,3,42,0.13)]
        ${isApplied ? 'border-brand-plum ring-2 ring-brand-lavender' : 'border-brand-midnight/15'}
        ${isLocked ? 'opacity-60' : ''}
      `}
      onClick={() => !isLocked && !isLoading && onSelect(template)}
    >
      {/* Preview — a live, scaled, display-only render of the actual template site
          (so the card shows the real WEBSITE design, not a stock house photo). Falls
          back to a thumbnail image, then a branded placeholder. */}
      <div className="relative aspect-[4/3] overflow-hidden border-b border-brand-midnight/10 bg-gradient-to-br from-brand-midnight to-brand-plum">
        {/* Always-present branded base so a dead/blank preview never reads as broken. */}
        <TemplateWireframe name={template.name} />
        {/* A real, scaled render of the template's actual website (blocks + palette). */}
        <div className="pointer-events-none absolute inset-0">
          <TemplateLivePreview template={template} />
        </div>

        {/* Applied badge */}
        {isApplied && (
          <div className="absolute top-2 left-2 flex items-center gap-1 rounded-full bg-brand-plum px-2 py-1 text-xs font-semibold text-white">
            <CheckCircle className="h-3 w-3" />
            Active
          </div>
        )}

        {/* Locked badge */}
        {isLocked && (
          <div className="absolute top-2 right-2 flex items-center gap-1 rounded-full bg-brand-lilac px-2 py-1 text-xs font-semibold text-brand-plum">
            <Lock className="h-3 w-3" />
            {PLAN_LABELS[template.plan_required]}+
          </div>
        )}

        {/* Hover overlay */}
        {!isLocked && (
          <div className="absolute inset-0 bg-brand-midnight/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
            <span className="text-white text-sm font-semibold">
              {isApplied ? 'Edit This Template' : 'Use This Template'}
            </span>
          </div>
        )}
      </div>

      {/* Info */}
      <div className="p-3">
        <p className="text-sm font-semibold text-brand-midnight truncate">{template.name}</p>
        {template.description && (
          <p className="text-xs text-brand-ink mt-0.5 line-clamp-2">{template.description}</p>
        )}
      </div>
    </div>
  );
}

// The legacy Simon Gray template is the old raw-HTML starter, superseded by the
// block templates — keep it out of the gallery. (The old /template-wizard still
// uses it as its "golden path", so we filter here in the dashboard, not at the API.)
function isLegacyHtmlTemplate(t: Pick<Template, 'slug' | 'name'>): boolean {
  const slug = (t.slug || '').toLowerCase();
  const name = (t.name || '').toLowerCase();
  return slug === 'home-sgr' || slug.includes('simon-gray') || name.includes('simon gray');
}

// A single restrained stat tile — tabular figures, quiet label, a small icon chip
// for legibility. Deliberately NOT the gradient-glow hero-metric template (an
// explicit anti-pattern in DESIGN.md); the lift + chip are the only flourish.
const STAT_CHIPS = {
  lavender: 'bg-brand-lilac text-brand-plum group-hover:bg-brand-lavender',
  teal: 'bg-brand-teal-soft text-brand-teal-deep group-hover:bg-brand-teal/40',
  sand: 'bg-brand-sand/40 text-brand-sand-deep group-hover:bg-brand-sand/60',
} as const;

function StatTile({ label, value, sub, icon: Icon, tint = 'lavender' }: { label: string; value: string; sub?: string; icon: typeof Eye; tint?: keyof typeof STAT_CHIPS }) {
  return (
    <div className="pop-card pop-card-hover group rounded-2xl bg-white p-5">
      <div className="flex items-center justify-between">
        <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-brand-ink-soft" style={MONTSERRAT}>{label}</p>
        <span className={`flex h-8 w-8 items-center justify-center rounded-xl ring-1 ring-brand-midnight/10 transition-colors ${STAT_CHIPS[tint]}`}>
          <Icon className="h-4 w-4" />
        </span>
      </div>
      <p className="mt-3 text-[2rem] font-semibold leading-none tabular-nums text-brand-midnight" style={MONTSERRAT}>{value}</p>
      {sub && <p className="mt-1.5 truncate text-xs text-brand-ink-soft">{sub}</p>}
    </div>
  );
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { tenantSlug, plan, email } = useAuthStore();
  // Which site the dashboard previews + hands off to the Studio. Defaults to the primary/active
  // site; on a multi-site account the switcher below lets the agent pick, and "Open in Studio"
  // opens exactly that site (?site=) so the preview and the builder always agree on the site.
  const { activeSite, activeSiteId, sites, hasMultiple, setActiveSiteId } = useActiveSite();

  const [prompt, setPrompt] = useState('');
  const [guidedOpen, setGuidedOpen] = useState(false);
  const [startOverOpen, setStartOverOpen] = useState(false); // confirm before re-running setup on an existing site
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  // Voice dictation appends to the prompt; the mic is hidden when unsupported.
  const voice = useVoiceInput((t) => setPrompt((prev) => (prev ? `${prev} ${t}` : t)));

  // Tap a suggestion → fill the box and focus it (cursor at end) so the agent can
  // personalize (add a brokerage name / city) before hitting Build.
  function fillPrompt(text: string) {
    setPrompt(text);
    requestAnimationFrame(() => {
      const el = textareaRef.current;
      if (el) { el.focus(); el.setSelectionRange(el.value.length, el.value.length); }
    });
  }

  // Just the agent's name for a friendly greeting (cheap, cached — the Profile page uses the same key).
  const { data: profile } = useQuery({ queryKey: ['profile'], queryFn: getProfile, staleTime: 5 * 60_000 });

  const { data: analytics, isError: analyticsError, refetch: refetchAnalytics } = useQuery({
    queryKey: ['analytics-summary'],
    queryFn: getAnalyticsSummary,
    retry: 1,
  });

  const { data: leadsData, isError: leadsError, refetch: refetchLeads } = useQuery({
    queryKey: ['leads', { page: 1, page_size: 5 }],
    queryFn: () => getLeads({ page: 1, page_size: 5 }),
    retry: 1,
  });

  const { data: templates = [], isLoading: templatesLoading } = useQuery({
    queryKey: ['templates'],
    queryFn: getTemplates,
    staleTime: 5 * 60 * 1000,
  });

  // Is this account's site new/empty? Used to tailor the hero — starter scaffold
  // prompts for an empty site vs. quick-edit prompts for an existing one.
  const { data: buildState } = useQuery({
    queryKey: ['site-build-state'],
    queryFn: getSiteBuildState,
    staleTime: 60 * 1000,
    retry: 1,
  });

  const applyMutation = useMutation({
    mutationFn: (slug: string) => applyTemplateBySlug(slug),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates'] });
      // Site content changed — refresh the build-state gate too.
      queryClient.invalidateQueries({ queryKey: ['site-build-state'] });
      // The Studio is the single builder — it edits blocks and everything else — so
      // a freshly-applied template opens there (not the legacy block design editor).
      navigate('/studio');
      toast({ description: 'Template applied. Customize it in the Studio.' });
    },
    onError: (err: Error) => {
      toast({ variant: 'destructive', description: err.message ?? 'Failed to apply template' });
    },
  });

  // Preview the SELECTED site (not always the tenant/primary slug) so the dashboard preview and the
  // Studio show the same site. A draft (non-primary) site only serves live once active, so its
  // live preview may be blank until it's live — the Studio still opens its draft to edit.
  const previewSlug = activeSite?.slug ?? tenantSlug;
  const siteUrl = previewSlug
    ? `https://${previewSlug}.${import.meta.env.VITE_ROOT_DOMAIN || 'searchproperties.io'}`
    : null;
  const openInStudio = () => navigate(activeSiteId ? `/studio?site=${activeSiteId}` : '/studio');

  // Templates shown in the gallery — the retired Simon Gray HTML template is hidden.
  const visibleTemplates = templates.filter((t) => !isLegacyHtmlTemplate(t));

  const hasNoTemplate = !templatesLoading && templates.length > 0 && !templates.some((t) => t.is_applied);
  // A site already exists (a template is applied).
  const hasAppliedSite = !templatesLoading && templates.some((t) => t.is_applied);
  // Bug 2: the setup wizard was left mid-way — offer to resume it (highest-priority CTA).
  const setupInProgress = isBuildInProgress();
  // Empty site → no published content yet (drives the lede + the "live vs draft" status).
  const siteIsEmpty = buildState?.isEmpty === true;
  // …but content isn't enough: a site only SERVES on its subdomain once sites.status is
  // 'active'. Primary sites are created active; every additional site starts as 'draft'
  // and only the Studio's Publish flips it. Without this check the dashboard showed a
  // green "Live" and "Your site is live" for a draft whose public URL was serving the
  // default template — the one place an agent would look to catch that was wrong.
  // Unknown (activeSite not loaded) keeps the old assumption rather than crying draft.
  const siteIsPublished = activeSite ? activeSite.status === 'active' : true;
  const siteIsLive = hasAppliedSite && !siteIsEmpty && siteIsPublished;

  // Greeting name: the agent's real first name (from their profile) is best. Fall back to the email
  // local-part ONLY when it reads like a given name — never greet with an account slug/acronym like
  // "LWR" (all-caps, or containing digits). Otherwise show a clean nameless greeting.
  const profileFirst = profile?.full_name?.trim().split(/\s+/)[0] || null;
  const emailRaw = email && email.includes('@') ? email.split('@')[0].replace(/[._-]+/g, ' ').trim().split(/\s+/)[0] : '';
  const emailFirst =
    /^[a-zA-Z]{2,15}$/.test(emailRaw) && emailRaw !== emailRaw.toUpperCase()
      ? emailRaw.replace(/\b\w/g, (c) => c.toUpperCase())
      : null;
  const firstName = profileFirst ?? emailFirst;
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';
  // A live site has finished setup — don't let a stale "setup in progress" localStorage flag
  // override the lede (same gate as the Resume banner below), or it reads as unfinished when it isn't.
  const ledeSubtitle = setupInProgress && !siteIsLive
    ? 'You have a setup in progress — pick up where you left off.'
    : siteIsEmpty
      ? "Let's build your site. Describe it below, or start from a template."
      : siteIsLive
        ? 'Your site is live. Here’s what’s happening, and what to do next.'
        : 'Your site isn’t published yet — open the Studio and hit Publish to put it online.';

  function handleCreateFromPrompt() {
    const trimmed = prompt.trim();
    if (!trimmed) return;
    // Every prompt opens the Studio and auto-runs there (the single builder).
    navigate(chooseBuildRoute(buildState?.isEmpty, trimmed));
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleCreateFromPrompt();
    }
  }

  function handleSelectTemplate(template: Template) {
    if (template.locked) return;
    // The template you're already using → just open the Studio to edit it. Don't
    // re-apply it: applying re-scaffolds the site and would wipe your customizations.
    if (template.is_applied) {
      navigate('/studio');
      return;
    }
    applyMutation.mutate(template.slug);
  }

  function runNextStep(step: (typeof NEXT_STEPS)[number]) {
    if (step.to) { navigate(step.to); return; }
    if (step.prompt) navigate(chooseBuildRoute(buildState?.isEmpty, step.prompt));
  }

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-6 lg:p-8">

      {/* ── Console band — the bold plum hero. Greeting + status + the AI prompt live
          here, so the dashboard OPENS with brand presence instead of text on white.
          The AI prompt is the same surface for empty + live sites (one place to type). ── */}
      <section
        data-tour="dash-ai-prompt"
        className="relative overflow-hidden rounded-3xl p-6 text-white shadow-[0_34px_80px_-36px_rgba(16,3,42,0.7)] sm:p-9"
        style={{ background: 'linear-gradient(150deg, #10032A 0%, #190733 55%, #2A0B4A 100%)' }}
      >
        <div aria-hidden className="pointer-events-none absolute inset-0">
          <div className="auth-glow absolute -right-24 -top-28 h-80 w-80 rounded-full opacity-50 blur-3xl" style={{ background: 'radial-gradient(circle, rgba(201,184,244,0.30), transparent 62%)' }} />
          <div className="auth-glow-slow absolute -bottom-32 -left-20 h-80 w-80 rounded-full opacity-40 blur-3xl" style={{ background: 'radial-gradient(circle, rgba(78,31,96,0.85), transparent 60%)' }} />
          <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: 'radial-gradient(rgba(201,184,244,0.9) 1px, transparent 1px)', backgroundSize: '22px 22px' }} />
        </div>

        {/* Floating accent chips — the playful signature, bright chunky tiles. */}
        <div aria-hidden className="pointer-events-none absolute right-7 top-7 hidden items-start gap-3 lg:flex">
          <span className="flex h-12 w-12 -rotate-6 items-center justify-center rounded-2xl bg-brand-sand text-brand-midnight shadow-[0_12px_26px_-8px_rgba(0,0,0,0.55)] ring-2 ring-white/20">
            <Home className="h-5 w-5" />
          </span>
          <span className="mt-6 flex h-12 w-12 rotate-6 items-center justify-center rounded-2xl bg-brand-lavender text-brand-midnight shadow-[0_12px_26px_-8px_rgba(0,0,0,0.55)] ring-2 ring-white/20">
            <Sparkles className="h-5 w-5" />
          </span>
        </div>

        <div className="relative">
          <div className="flex flex-wrap items-center gap-2">
            {plan && (
              <span className="rounded-full bg-white/10 px-2.5 py-0.5 text-xs font-medium capitalize text-brand-lavender ring-1 ring-white/10" style={MONTSERRAT}>
                {PLAN_LABELS[plan] ?? plan}
              </span>
            )}
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-2.5 py-0.5 text-xs font-medium text-brand-lavender ring-1 ring-white/10">
              <span className={`h-1.5 w-1.5 rounded-full ${siteIsLive ? 'bg-brand-teal' : 'bg-brand-sand'}`} />
              {siteIsLive ? 'Live' : siteIsEmpty ? 'Not built yet' : 'Draft'}
            </span>
          </div>

          <h1 className="mt-3.5 text-[2rem] font-semibold leading-[1.04] tracking-[-0.02em] sm:text-[2.6rem]" style={MONTSERRAT}>
            {greeting}{firstName ? `, ${firstName}` : ''}
          </h1>
          <p className="mt-2 max-w-xl text-[15px] leading-relaxed text-brand-lavender/85">{ledeSubtitle}</p>

          {/* AI prompt — the single place to type (full on empty, one-line on live) */}
          <div className="mt-7 flex gap-2">
            <div className="relative flex-1">
              <textarea
                ref={textareaRef}
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                onKeyDown={handleKeyDown}
                rows={siteIsEmpty ? 2 : 1}
                placeholder={siteIsEmpty
                  ? 'e.g. Luxury real estate site for Palm Beach, deep navy with gold accents, very high end…'
                  : 'Make a change with AI — “add a contact page”, “write my bio”…'}
                className="w-full resize-none rounded-xl border border-white/20 bg-white/10 px-4 py-3 pr-12 text-sm text-white placeholder:text-white/50 backdrop-blur-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-lavender focus-visible:ring-offset-2 focus-visible:ring-offset-brand-midnight"
              />
              {voice.supported && (
                <button
                  type="button"
                  onClick={voice.toggle}
                  aria-label={voice.recording ? 'Stop voice input' : 'Start voice input'}
                  title={voice.recording ? 'Stop' : 'Speak your idea'}
                  className={`absolute bottom-2 right-2 rounded-lg p-2 transition-colors ${
                    voice.recording ? 'animate-pulse bg-white/15 text-brand-sand' : 'text-white/70 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  {voice.recording ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
                </button>
              )}
            </div>
            <button
              onClick={handleCreateFromPrompt}
              disabled={!prompt.trim()}
              className="flex items-center gap-2 self-end rounded-xl bg-brand-lavender px-5 py-3 text-sm font-semibold text-brand-midnight shadow-[0_10px_28px_-10px_rgba(201,184,244,0.85)] transition hover:bg-white disabled:cursor-not-allowed disabled:opacity-40"
              style={MONTSERRAT}
            >
              <Send className="h-4 w-4" />
              {siteIsEmpty ? 'Build' : 'Go'}
            </button>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="shrink-0 text-xs text-brand-lavender/75">{siteIsEmpty ? 'Need ideas?' : 'Quick edits:'}</span>
            {(siteIsEmpty ? START_PROMPTS : EDIT_PROMPTS).map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => fillPrompt(s)}
                className="rounded-full border border-white/15 bg-white/5 px-3 py-1 text-xs text-white/90 transition-colors hover:bg-white/15 hover:text-white"
              >
                {s}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setGuidedOpen(true)}
              className="ml-auto inline-flex shrink-0 items-center gap-1.5 text-xs font-medium text-brand-lavender transition-colors hover:text-white"
            >
              <Wand2 className="h-3.5 w-3.5" />
              {siteIsEmpty ? 'Not sure what to type?' : 'Not sure?'}
            </button>
          </div>
        </div>
      </section>

      {/* ── Resume setup — the single highest-priority next action, only mid-wizard on a not-yet-live
             site. A live site has effectively finished setup, so a stale localStorage flag shouldn't
             keep nagging "Resume setting up your site". ── */}
      {setupInProgress && !siteIsLive && (
        <div className="flex items-center gap-4 rounded-xl border border-brand-border bg-brand-surface px-5 py-4">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-lilac">
            <Wand2 className="h-5 w-5 text-brand-plum" />
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-brand-midnight" style={MONTSERRAT}>Resume setting up your site</p>
            <p className="mt-0.5 text-sm text-brand-ink">Pick up right where you left off.</p>
          </div>
          <Button
            size="sm"
            className="shrink-0 bg-brand-plum text-white hover:bg-brand-plum-deep"
            onClick={() => navigate('/build')}
          >
            Resume setup
          </Button>
        </div>
      )}

      {/* ── Site showpiece — a framed LIVE preview of the agent's site ──────── */}
      {siteUrl && !siteIsEmpty && (
        <section className="overflow-hidden rounded-2xl border border-brand-border bg-white shadow-[0_24px_60px_-20px_rgba(16,3,42,0.35)]">
          <div className="grid lg:grid-cols-5">
            {/* Framed live preview — browser chrome + a scaled, display-only iframe */}
            <div className="border-b border-brand-surface-alt lg:col-span-3 lg:border-b-0 lg:border-r">
              <div className="flex items-center gap-2 bg-brand-surface px-4 py-2.5">
                <span className="flex gap-1.5">
                  <span className="h-2.5 w-2.5 rounded-full bg-brand-sand" />
                  <span className="h-2.5 w-2.5 rounded-full bg-brand-lavender" />
                  <span className="h-2.5 w-2.5 rounded-full bg-brand-teal" />
                </span>
                <span className="flex-1 truncate rounded-md border border-brand-border bg-white px-3 py-1 text-xs text-brand-ink">
                  {siteUrl.replace(/^https:\/\//, '')}
                </span>
              </div>
              <div className="relative aspect-[16/10] overflow-hidden bg-gradient-to-br from-brand-midnight to-brand-plum">
                {/* Loads behind a plum wash so it never reads as blank while fetching. */}
                <iframe
                  src={siteUrl}
                  title="Live preview of your site"
                  loading="lazy"
                  tabIndex={-1}
                  aria-hidden="true"
                  scrolling="no"
                  className="pointer-events-none absolute left-0 top-0 h-[200%] w-[200%] origin-top-left scale-50 border-0"
                />
              </div>
            </div>
            {/* Identity + actions */}
            <div className="flex flex-col justify-center gap-6 p-6 lg:col-span-2 lg:p-8">
              <div>
                <Badge
                  variant="outline"
                  className={siteIsLive ? 'border-brand-teal text-brand-teal-deep' : 'border-brand-border text-brand-ink'}
                >
                  {siteIsLive ? '● Live' : 'Draft'}
                </Badge>
                <h2 className="mt-3 text-2xl font-semibold tracking-tight text-brand-midnight" style={MONTSERRAT}>
                  {activeSite?.name ?? (firstName ? `${firstName}’s site` : 'Your site')}
                </h2>
                {hasMultiple && (
                  <div className="mt-2">
                    <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-brand-ink-soft">
                      Previewing
                    </label>
                    <select
                      aria-label="Choose which site to preview and open in the Studio"
                      value={activeSite?.id ?? ''}
                      onChange={(e) => setActiveSiteId(e.target.value)}
                      className="w-full max-w-[240px] truncate rounded-md border border-brand-border bg-white py-1.5 pl-3 pr-8 text-sm font-semibold text-brand-midnight focus:border-brand-lavender focus:outline-none focus:ring-2 focus:ring-brand-lavender"
                    >
                      {sites.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}{s.is_primary ? ' · primary' : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <a
                  href={siteUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-1 block truncate text-sm text-brand-plum hover:underline"
                >
                  {siteUrl.replace(/^https:\/\//, '')}
                </a>
              </div>
              <div className="space-y-2">
                <Button
                  className="w-full gap-1.5 bg-brand-plum text-white shadow-[0_8px_20px_-8px_rgba(78,31,96,0.7)] hover:bg-brand-plum-deep"
                  onClick={openInStudio}
                >
                  <Wand2 className="h-4 w-4" />
                  Open in Studio
                </Button>
                <Button
                  variant="outline"
                  className="w-full gap-1.5 border-brand-plum text-brand-plum hover:bg-brand-lilac-soft"
                  onClick={() => navigate(activeSiteId ? `/build?site=${activeSiteId}` : '/build')}
                  title="Run the guided setup wizard for this site"
                >
                  <Sparkles className="h-4 w-4" />
                  Set up your site
                </Button>
                <div className="flex gap-2">
                  <a href={siteUrl} target="_blank" rel="noopener noreferrer" className="flex-1">
                    <Button variant="outline" className="w-full gap-1.5 border-brand-border text-brand-plum">
                      <ExternalLink className="h-4 w-4" />
                      Preview
                    </Button>
                  </a>
                  <Button
                    variant="ghost"
                    className="flex-1 gap-1.5 text-brand-ink hover:text-brand-plum"
                    onClick={() => navigate('/sites')}
                  >
                    <Layers3 className="h-4 w-4" />
                    My Sites
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}


      {/* A failed fetch must not masquerade as "zero activity" — surface it with a retry. */}
      {(analyticsError || leadsError) && (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-brand-border bg-brand-surface px-4 py-2.5 text-sm">
          <span className="text-brand-ink">Some of your stats couldn’t load just now — that’s not the same as zero activity.</span>
          <button
            type="button"
            onClick={() => { void refetchAnalytics(); void refetchLeads(); }}
            className="shrink-0 font-medium text-brand-plum hover:underline"
          >
            Retry
          </button>
        </div>
      )}

      {/* ── Stats — restrained tabular tiles (no hero-metric glow) ──────────── */}
      <section data-tour="dash-stats" className="space-y-3">
        <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-brand-ink-soft" style={MONTSERRAT}>At a glance</p>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          <StatTile icon={Eye} tint="lavender" label="Site visits · 30d" value={analytics?.visits_30d?.toLocaleString() ?? '—'} />
          <StatTile icon={Users} tint="teal" label="New leads · 30d" value={analytics?.leads_30d?.toLocaleString() ?? '—'} />
          <StatTile
            icon={FileText}
            tint="sand"
            label="Top page"
            value={analytics?.top_pages?.[0]?.title ?? '—'}
            sub={analytics?.top_pages?.[0] ? `${analytics.top_pages[0].views.toLocaleString()} views` : undefined}
          />
        </div>
      </section>

      {/* ── Recent leads + Next steps ───────────────────────────────────────── */}
      <div className="grid gap-6 lg:grid-cols-3">
        <Card data-tour="dash-recent-leads" className="pop-card rounded-2xl lg:col-span-2">
          <CardContent className="p-6">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-base font-semibold text-brand-midnight" style={MONTSERRAT}>Recent leads</h2>
              <Button variant="ghost" size="sm" className="gap-1 text-sm text-brand-ink hover:text-brand-plum" onClick={() => navigate('/leads')}>
                View all
                <ArrowRight className="h-3.5 w-3.5" />
              </Button>
            </div>
            {leadsError ? (
              <div className="py-10 text-center">
                <p className="text-sm font-medium text-brand-midnight">Couldn’t load your leads</p>
                <button type="button" onClick={() => void refetchLeads()} className="mt-1 text-sm font-medium text-brand-plum hover:underline">
                  Retry
                </button>
              </div>
            ) : leadsData?.leads?.length ? (
              <div className="divide-y divide-brand-border">
                {leadsData.leads.map((lead) => (
                  <div key={lead.id} className="flex items-center gap-3 py-2.5">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-lilac text-sm font-semibold text-brand-plum">
                      {lead.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-brand-midnight">{lead.name}</p>
                      <p className="truncate text-xs text-brand-ink-soft">{lead.email}</p>
                    </div>
                    <div className="shrink-0 text-right">
                      <Badge variant="outline" className="border-brand-border text-xs text-brand-ink">{lead.source}</Badge>
                      <p className="mt-0.5 text-xs text-brand-ink-soft">{formatRelativeTime(lead.created_at)}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-10 text-center">
                <span className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-brand-lilac">
                  <Users className="h-5 w-5 text-brand-plum" />
                </span>
                <p className="text-sm font-medium text-brand-midnight">No leads yet</p>
                <p className="mx-auto mt-1 max-w-xs text-sm text-brand-ink">
                  They’ll appear here once visitors fill out your contact form.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Next steps — a small, finite set of suggested actions, not a widget wall */}
        <Card className="pop-card rounded-2xl">
          <CardContent className="p-6">
            <h2 className="mb-3 text-base font-semibold text-brand-midnight" style={MONTSERRAT}>Next steps</h2>
            <div className="space-y-1">
              {NEXT_STEPS.map((step) => (
                <button
                  key={step.label}
                  type="button"
                  onClick={() => runNextStep(step)}
                  className="group flex w-full items-center gap-3 rounded-lg px-2 py-2.5 text-left transition-colors hover:bg-brand-surface"
                >
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-brand-lilac text-brand-plum">
                    <step.icon className="h-4 w-4" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-brand-midnight">{step.label}</span>
                    <span className="block truncate text-xs text-brand-ink-soft">{step.hint}</span>
                  </span>
                  <ArrowRight className="h-4 w-4 shrink-0 text-brand-ink-soft transition-colors group-hover:text-brand-plum" />
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ── Template gallery ────────────────────────────────────────────────── */}
      <div data-tour="dash-template-gallery">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-base font-semibold text-brand-midnight" style={MONTSERRAT}>
            {hasNoTemplate ? 'Start from a template' : 'Switch template'}
          </h2>
          {hasAppliedSite && (
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="sm" className="text-sm text-brand-ink-soft hover:text-brand-error" onClick={() => setStartOverOpen(true)}>
                Start over
              </Button>
            </div>
          )}
        </div>

        {templatesLoading ? (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => <TemplateSkeleton key={i} />)}
          </div>
        ) : visibleTemplates.length === 0 ? (
          <div className="rounded-xl border border-brand-border bg-white py-10 text-center text-sm text-brand-ink">
            No templates yet. Check back soon.
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {visibleTemplates.slice(0, 8).map((t) => (
              <TemplateCard
                key={t.id}
                template={t}
                onSelect={handleSelectTemplate}
                isLoading={applyMutation.isPending && applyMutation.variables === t.slug}
              />
            ))}
          </div>
        )}
      </div>

      {/* Confirm before re-running setup on an existing site (it replaces the site). */}
      <AlertDialog open={startOverOpen} onOpenChange={setStartOverOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Start over and replace your current site?</AlertDialogTitle>
            <AlertDialogDescription>
              This re-runs the template setup wizard. When you finish and go live, it replaces
              your current site — pages, sections, and content. This can’t be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep my site</AlertDialogCancel>
            <AlertDialogAction
              className="bg-brand-error text-white hover:bg-[#A93226]"
              onClick={() => {
                setStartOverOpen(false);
                navigate('/build');
              }}
            >
              Yes, start over
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <GuidedStartDialog open={guidedOpen} onOpenChange={setGuidedOpen} />
    </div>
  );
}
